import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ShieldCheck, Lock, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function SecureUpgrade() {
  const navigate = useNavigate();
  const { user, refreshAuth, isAdmin, isModerator } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [requiresUpgrade, setRequiresUpgrade] = useState(false);

  const isProfessional = isAdmin || isModerator;
  const minPasswordLength = isProfessional ? 14 : 12;

  useEffect(() => {
    const checkUpgradeStatus = async () => {
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("require_password_reset, promoted_to_practitioner_at")
        .eq("id", user.id)
        .single();

      if (!profile?.require_password_reset) {
        navigate("/home");
      } else {
        setRequiresUpgrade(true);
      }
    };

    checkUpgradeStatus();
  }, [user, navigate]);

  const validatePassword = (password: string): boolean => {
    if (password.length < minPasswordLength) {
      setPasswordError(`Le mot de passe doit contenir au moins ${minPasswordLength} caractères`);
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError("Les mots de passe ne correspondent pas");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword(newPassword)) return;

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          require_password_reset: false,
          secure_upgrade_completed_at: new Date().toISOString(),
        })
        .eq("id", user?.id);

      if (profileError) throw profileError;

      toast.success("Mot de passe mis à jour avec succès ! 🎉");
      await refreshAuth();

      if (isAdmin) {
        navigate("/admin/practitioners");
      } else if (isModerator) {
        navigate("/moderation");
      } else {
        navigate("/home");
      }
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la mise à jour du mot de passe");
    } finally {
      setLoading(false);
    }
  };

  if (!requiresUpgrade) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center"><Logo /></div>
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
            <CardTitle className="text-2xl text-center">Sécurisation du compte</CardTitle>
          </div>
          <CardDescription className="text-center">
            Veuillez créer un nouveau mot de passe sécurisé
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-6">
            <Lock className="h-4 w-4" />
            <AlertDescription>
              Mot de passe d'au moins <strong>{minPasswordLength} caractères</strong> requis.
            </AlertDescription>
          </Alert>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setPasswordError(""); }}
                  placeholder={`Minimum ${minPasswordLength} caractères`}
                  required disabled={loading} minLength={minPasswordLength}
                />
                <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmer</Label>
              <Input id="confirm-password" type={showPassword ? "text" : "password"} value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(""); }}
                placeholder="Confirmez votre mot de passe" required disabled={loading} minLength={minPasswordLength}
              />
            </div>
            {passwordError && <p className="text-sm text-destructive font-medium">{passwordError}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Mise à jour..." : "Mettre à jour mon mot de passe"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
